import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Eye, Mail, FileText, Send, Loader2, Plus, Undo2 } from 'lucide-react';
import type { ChangeOrder, ProjectPLBucket } from '../../types';
import { calculateTotals } from '../../utils/proposalCalculations';
import { getChangeOrderReference } from '../../utils/changeOrders';
import './ProposalReviewModal.css';

const BUCKET_LABELS: Record<ProjectPLBucket, string> = {
  design: 'Design',
  engineering: 'Engineering',
  timber: 'Timber / Materials',
  production: 'Production',
  freight: 'Freight',
  install: 'Install',
  travel: 'Travel',
  overhead: 'Overhead',
  unassigned: 'Other',
};

const COUNTDOWN_SECONDS = 7;
type Tab = 'details' | 'client' | 'email';
const ALL_TABS: Tab[] = ['details', 'client', 'email'];

const fmt = (n: number) => `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export type ChangeOrderReviewSendMode = 'release' | 'email_only';

interface ChangeOrderReviewModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (customMessage: string, recipients: string[]) => Promise<void>;
  /** release = mark sent + email (draft). email_only = resend link for an already-released CO. */
  sendMode?: ChangeOrderReviewSendMode;
  changeOrder: ChangeOrder;
  clientName: string;
  priorContractValue: number;
  sending: boolean;
  savedMessage: string;
  onMessageChange: (msg: string) => void;
  savedRecipients: string[];
  onRecipientsChange: (recipients: string[]) => void;
}

const ChangeOrderReviewModal: React.FC<ChangeOrderReviewModalProps> = ({
  open,
  onClose,
  onSend,
  sendMode = 'release',
  changeOrder,
  clientName,
  priorContractValue,
  sending,
  savedMessage,
  onMessageChange,
  savedRecipients,
  onRecipientsChange,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('details');
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const [visitedTabs, setVisitedTabs] = useState<Set<Tab>>(() => new Set<Tab>(['details']));
  const allTabsVisited = ALL_TABS.every(t => visitedTabs.has(t));

  const handleTabClick = useCallback((tab: Tab) => {
    setActiveTab(tab);
    setVisitedTabs(prev => {
      if (prev.has(tab)) return prev;
      const next = new Set(prev);
      next.add(tab);
      return next;
    });
  }, []);

  // Countdown send (matches proposal flow).
  const [countdown, setCountdown] = useState<number | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownMessageRef = useRef('');
  const countdownRecipientsRef = useRef<string[]>([]);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setCountdown(null);
  }, []);

  const handleStartCountdown = useCallback(() => {
    countdownMessageRef.current = savedMessage;
    countdownRecipientsRef.current = [...savedRecipients];
    setCountdown(COUNTDOWN_SECONDS);
  }, [savedMessage, savedRecipients]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      clearCountdown();
      onSend(countdownMessageRef.current, countdownRecipientsRef.current);
      return;
    }
    countdownRef.current = setInterval(() => {
      setCountdown(prev => (prev !== null ? prev - 1 : null));
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [countdown, clearCountdown, onSend]);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const reference = getChangeOrderReference(changeOrder);
  const delta = changeOrder.deltaAmount || 0;
  const newContractValue = priorContractValue + delta;

  const rows = useMemo(() => {
    const co = changeOrder;
    if (co.pricingMode === 'line_item' && co.lineItems) {
      return co.lineItems.map(li => ({
        desc: li.description || 'Item',
        cat: BUCKET_LABELS[li.bucket] || 'Other',
        amount: li.amount,
      }));
    }
    if (co.pricingMode === 'builder' && co.builderNodeSets) {
      const margin = co.builderProfitMargin ?? 0;
      return co.builderNodeSets
        .filter(ns => ns.enabled !== false)
        .map(ns => ({
          desc: ns.name || 'Structure',
          cat: 'Structure',
          amount: calculateTotals([ns], margin).billableTotal,
        }));
    }
    return [{ desc: co.title || 'Change order', cat: 'Lump sum', amount: delta }];
  }, [changeOrder, delta]);

  const recipients = savedRecipients;
  const customMessage = savedMessage;
  const companyName = 'LC Timber Frames';
  const signLink = `${window.location.origin}/change-order/${changeOrder.slug}`;

  const handleAddEmail = () => {
    const email = newEmail.trim();
    if (!email) return;
    if (!isValidEmail(email)) {
      setEmailError('Invalid email address');
      return;
    }
    if (recipients.includes(email)) {
      setEmailError('Already added');
      return;
    }
    onRecipientsChange([...recipients, email]);
    setNewEmail('');
    setEmailError('');
  };

  const handleRemoveEmail = (email: string) => {
    onRecipientsChange(recipients.filter(r => r !== email));
  };

  const handleAddKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const messageHtml = (customMessage || '')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');

  const emailHtml = `
    <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 560px; margin: 0 auto; padding: 32px 24px;">
      <div style="background: #2C3E2F; padding: 24px; border-radius: 10px 10px 0 0; text-align: center;">
        <h1 style="color: white; margin: 0; font-size: 20px; letter-spacing: 1px;">${companyName}</h1>
        <p style="color: rgba(255,255,255,0.7); margin: 4px 0 0; font-size: 12px;">CHANGE ORDER ${reference}</p>
      </div>
      <div style="background: white; padding: 28px 24px; border: 1px solid #E2E0DB; border-top: none; border-radius: 0 0 10px 10px;">
        <p style="color: #4F423A; line-height: 1.6; margin: 0 0 20px; white-space: pre-wrap;">${messageHtml || `Hi ${clientName.split(' ')[0] || 'there'},<br/><br/>A change order is ready for your review and approval.`}</p>
        <div style="background: #F7F5F0; border: 1px solid #E2E0DB; border-radius: 8px; padding: 16px 20px; margin: 0 0 20px;">
          <p style="margin: 0 0 6px; font-size: 13px; color: #6B6960;">${changeOrder.title || 'Change order'}</p>
          <p style="margin: 0; font-size: 13px; color: #6B6960;">This change order: <strong style="color:#2C3E2F;">${delta >= 0 ? '+' : ''}${fmt(delta)}</strong></p>
          <p style="margin: 6px 0 0; font-size: 13px; color: #6B6960;">New contract value: <strong style="color:#2C3E2F;">${fmt(newContractValue)}</strong></p>
        </div>
        <a href="${signLink}" style="display: inline-block; padding: 14px 32px; background: #2C3E2F; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 14px;">
          Review &amp; Approve Change Order
        </a>
        <p style="color: #9C9889; font-size: 12px; margin: 20px 0 0;">
          You can review the full details and sign securely online.
        </p>
      </div>
    </div>
  `;

  const emailOnly = sendMode === 'email_only';

  if (!open) return null;

  return (
    <div className="prm-overlay" onClick={onClose}>
      <div className="prm-modal" onClick={e => e.stopPropagation()}>
        <div className="prm-header">
          <div>
            <h2>{emailOnly ? 'Send Change Order Email' : 'Review & Send Change Order'}</h2>
            <p className="prm-snapshot-subtitle">{reference} · {changeOrder.title}</p>
          </div>
          <button className="prm-close" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="prm-tabs">
          <button className={`prm-tab ${activeTab === 'details' ? 'active' : ''}`} onClick={() => handleTabClick('details')}>
            <FileText size={15} /> Details
            {!visitedTabs.has('details') && <span className="prm-tab-dot" />}
          </button>
          <button className={`prm-tab ${activeTab === 'client' ? 'active' : ''}`} onClick={() => handleTabClick('client')}>
            <Eye size={15} /> Client View
            {!visitedTabs.has('client') && <span className="prm-tab-dot" />}
          </button>
          <button className={`prm-tab ${activeTab === 'email' ? 'active' : ''}`} onClick={() => handleTabClick('email')}>
            <Mail size={15} /> Email Preview
            {!visitedTabs.has('email') && <span className="prm-tab-dot" />}
          </button>
        </div>

        <div className="prm-body">
          {activeTab === 'details' && (
            <div className="prm-internal">
              <div className="prm-summary-banner">
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">Current contract</span>
                  <span className="prm-stat-value">{fmt(priorContractValue)}</span>
                </div>
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">This change order</span>
                  <span className={`prm-stat-value ${delta < 0 ? 'prm-profit' : 'prm-highlight'}`}>
                    {delta >= 0 ? '+' : ''}{fmt(delta)}
                  </span>
                </div>
                <div className="prm-summary-stat">
                  <span className="prm-stat-label">New contract value</span>
                  <span className="prm-stat-value prm-highlight">{fmt(newContractValue)}</span>
                </div>
              </div>

              {changeOrder.scopeDescription && (
                <p className="prm-margin-note" style={{ marginTop: 0 }}>
                  <strong>Scope:</strong> {changeOrder.scopeDescription}
                </p>
              )}

              <div className="prm-table-wrap">
                <table className="prm-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Category</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td className="prm-name-cell">{r.desc}</td>
                        <td>{r.cat}</td>
                        <td className="prm-billable-cell">{fmt(r.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="prm-totals-final">
                      <td><strong>Change total</strong></td>
                      <td></td>
                      <td className="prm-billable-cell"><strong>{delta >= 0 ? '+' : ''}{fmt(delta)}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'client' && (
            <div className="prm-client-view">
              <iframe
                src={`/change-order/${changeOrder.slug}?preview=1`}
                title="Change Order Client Preview"
                className="prm-iframe"
              />
            </div>
          )}

          {activeTab === 'email' && (
            <div className="prm-email-tab">
              <div className="prm-email-meta">
                <div className="prm-email-field prm-email-to-field">
                  <label>To</label>
                  <div className="prm-recipients-wrap">
                    <div className="prm-recipient-chips">
                      {recipients.map(email => (
                        <span key={email} className="prm-recipient-chip">
                          {email}
                          <button className="prm-chip-remove" onClick={() => handleRemoveEmail(email)} title="Remove">
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                      {recipients.length === 0 && (
                        <span className="prm-email-error">Add at least one recipient</span>
                      )}
                    </div>
                    <div className="prm-add-recipient">
                      <input
                        type="email"
                        className="prm-add-email-input"
                        placeholder="Add email address..."
                        value={newEmail}
                        onChange={e => { setNewEmail(e.target.value); setEmailError(''); }}
                        onKeyDown={handleAddKeyDown}
                      />
                      <button className="prm-add-email-btn" onClick={handleAddEmail} disabled={!newEmail.trim()}>
                        <Plus size={14} /> Add
                      </button>
                    </div>
                    {emailError && <span className="prm-email-error">{emailError}</span>}
                  </div>
                </div>
                <div className="prm-email-field">
                  <label>Subject</label>
                  <span>Change order {reference} is ready for your approval</span>
                </div>
              </div>

              <div className="prm-email-edit">
                <label>Message (editable)</label>
                <textarea
                  value={customMessage}
                  onChange={e => onMessageChange(e.target.value)}
                  rows={6}
                  className="prm-email-textarea"
                />
              </div>

              <div className="prm-email-preview-wrap">
                <label>Email Preview</label>
                <div className="prm-email-preview" dangerouslySetInnerHTML={{ __html: emailHtml }} />
              </div>
            </div>
          )}
        </div>

        <div className="prm-footer">
          {countdown !== null ? (
            <div className="prm-countdown-bar">
              <div className="prm-countdown-left">
                <svg className="prm-countdown-ring" viewBox="0 0 36 36" width="36" height="36">
                  <circle className="prm-ring-bg" cx="18" cy="18" r="15.5" fill="none" stroke="#E8E5E0" strokeWidth="3" />
                  <circle
                    className="prm-ring-progress"
                    cx="18" cy="18" r="15.5"
                    fill="none" stroke="#2C3E2F" strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 15.5}
                    strokeDashoffset={2 * Math.PI * 15.5 * (1 - countdown / COUNTDOWN_SECONDS)}
                    transform="rotate(-90 18 18)"
                  />
                </svg>
                <span className="prm-countdown-text">
                  Sending in <strong>{countdown}s</strong>
                </span>
              </div>
              <button className="prm-btn-undo" onClick={clearCountdown}>
                <Undo2 size={15} /> Undo
              </button>
            </div>
          ) : sending ? (
            <div className="prm-countdown-bar prm-sending-bar">
              <div className="prm-countdown-left">
                <Loader2 size={20} className="prm-spin" />
                <span className="prm-countdown-text">{emailOnly ? 'Sending email…' : 'Sending change order…'}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="prm-footer-meta">
                <span className="prm-footer-client">{clientName}</span>
                <span className="prm-footer-email">{recipients.length} recipient{recipients.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="prm-footer-actions">
                <button className="prm-btn-cancel" onClick={onClose}>Cancel</button>
                {!allTabsVisited && (
                  <span className="prm-send-hint">Review all tabs before sending</span>
                )}
                <button
                  className="prm-btn-send"
                  onClick={handleStartCountdown}
                  disabled={!allTabsVisited || recipients.length === 0}
                  title={!allTabsVisited ? 'Visit all 3 tabs to enable sending' : recipients.length === 0 ? 'Add a recipient' : ''}
                >
                  <Send size={16} /> {emailOnly ? 'Send Email' : 'Send Change Order'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeOrderReviewModal;
